import React, { useContext, useState, useRef, useEffect } from 'react'

import { useForm } from "react-hook-form";
import moment from "moment"
import nl2br from 'react-nl2br'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faPaperPlane, faCloudUploadAlt, faQuestionCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { loadArticle } from '../../services/articles'
import AppContext from '../../contexts/AppContext'
import useToastContext from "../../hooks/useToastContext"
import { Modal } from '../UI'
import ReactHtmlParser from 'react-html-parser'
import { OverlayTrigger, Popover, Alert } from 'react-bootstrap'

const Comment = ({ comment, onUpdate, level }) => {

    const { commentServices, authServices, data, setAppState } = useContext(AppContext)
    const { app } = data
    const { user } = app
    const [ isOpen, setIsOpen ] = useState(false)

	level = level ? level : 0

	const addToast = useToastContext()

	const [ isEditing, setIsEditing ] = useState(false)
	const [ loading, setLoading ] = useState(false)

	const isHidden = [0, 2, 3].includes(comment.status)
	const canShowCard = ![0].includes(comment.status) || comment.user.str_id == app.user_str_id

	const canEdit = authServices.isLoggedIn() && (user.id == comment.user_id || user.type == 'admin') && !isHidden
	const canDelete = authServices.isLoggedIn() && (user.id == comment.user_id || user.type == 'admin') && comment.status == 1
	const canRestore = authServices.isLoggedIn() && (
		(user.id == comment.user_id && [2].includes(comment.status)) ||
		(user.type == 'admin' && [2, 3].includes(comment.status))
	)
	const canAnswer = level < 10

	const commentDelete = () => {
		setLoading(true)
		return commentServices.delete(comment.id).then(res => {
			onUpdate().then(() => setLoading(false))
		}).catch(res => {
			addToast({title: 'Error', message: res.data.message, type: 'danger'})
			setLoading(false)
		})
	}

	const commentRestore = () => {
		setLoading(true)
		return commentServices.restore(comment.id).then(res => {
			onUpdate().then(() => setLoading(false))
		}).catch(res => {
			addToast({title: 'Error', message: res.data.message, type: 'danger'})
			setLoading(false)
		})
	}

	if(!canShowCard) return null

	return (
		<>
			{isEditing &&
				<CommentEdit
					showLoading={loading}
					comment={comment}
					onCancel={() => setIsEditing(false)}
					onUpdate={() => {
						setLoading(true)
						onUpdate().then(() => {
							setIsEditing(false)
							setLoading(false)
						})
					}}
				/>
			}
			{!isEditing &&
				<div className="card mb-3" style={{opacity: isHidden ? 0.65 : 1}} id={'comment_' +comment.id}>
					<div className="card-body px-3 py-2">
						<div className="mb-2">
							<div className="d-flex flex-wrap">
								<small
									className={'font-weight-bold me-2' + (comment.user?.type == 'admin' ? ' text-danger' : '')}
									style={{textDecoration: (comment.user ? '' : 'line-through')}}
								>{comment.user?.full_name ? comment.user.full_name : ('User #' + comment.user_id)}{comment.user?.type == 'admin' ? ' (admin)' : ''}</small>
								<small className="text muted me-2">posted {moment(comment.created_at).fromNow()}</small>
								{comment.created_at != comment.updated_at &&
									<small className="text muted me-2">updated {moment(comment.updated_at).fromNow()}</small>
								}
								{canEdit && !loading &&
									<>
										<small className="me-2">∙</small>
										<small className="text muted me-2">
											<a href="" role="button" onClick={e => {
												e.preventDefault()
												setIsEditing(true)
											}}>Edit</a>
										</small>
									</>
								}
								{canDelete && !loading &&
									<>
										<small className="me-2">∙</small>
										<small className="text muted me-2">
											<a href="" role="button" onClick={e => {
												e.preventDefault()
												commentDelete()
											}}>Delete</a>
										</small>
									</>
								}
								{canRestore && !loading &&
									<>
										<small className="me-2">∙</small>
										<small className="text muted me-2">
											<a href="" role="button" onClick={e => {
												e.preventDefault()
												commentRestore()
											}}>Restore</a>
										</small>
									</>
								}

								{loading ? <>
									<small className="me-2">∙</small>
									<small className="text muted me-2">
										<FontAwesomeIcon icon={faSpinner} spin />
									</small>
								</> : null}
							</div>
						</div>
						{isHidden &&
							<div className="mb-0 text-muted">
								{comment.status == 0 &&
									<span>Your comment is hidden until you verify it.</span>
								}
								{[2, 3].includes(comment.status) && 'This message has been deleted from the post view. '}
							</div>
						}

						{!isHidden &&
							<div className="mb-2">
								{nl2br(comment.message)}
							</div>
						}
						{!isHidden && canAnswer &&
							<div className="mb-2">
								<span
									role="button"
									className="cursor-pointer link-dotted"
									role="button"
									onClick={() => {
										setIsOpen(!isOpen)
									}}
								><small>Answer to this comment</small></span>
							</div>
						}
					</div>
				</div>
			}
			{((comment.comments && comment.comments.length > 0) || isOpen) &&
				<div className="comments-block">
					{canAnswer && isOpen &&
						<CommentForm
							type={comment.type}
							commentable_id={comment.id}
							commentable_type="App\Comment"
							onUpdate={() => {
								return onUpdate().then(() => {
									setIsOpen(false)
								})
							}}
						/>
					}
					{comment.comments && comment.comments.length > 0 && comment.comments.map(comment =>
						<Comment
							key={comment.id}
							comment={comment}
							onUpdate={onUpdate}
							level={level + 1}
						/>
					)}
				</div>
			}
		</>
	)
}

const CommentEdit = ({ showLoading, comment, onUpdate, onCancel }) => {

    const { commentServices } = useContext(AppContext)

	const [ loading, setLoading ] = useState(false)

    const { register, handleSubmit, watch, errors, setError, reset } = useForm();

	const addToast = useToastContext()

	const onSubmit = data => {
		setLoading(true)
		return commentServices.update(comment.id, data).then(res => {
			reset()
			onUpdate()
			setLoading(false)
		}).catch(res => {
			addToast({title: 'Error', message: res.data.message, type: 'danger'})
			setLoading(false)
		})
	}

	return (
		<form className="mb-3" onSubmit={handleSubmit(onSubmit)}>
			<div>
				<textarea
					className="form-control mb-2"
					rows="2"
					name="message"
					defaultValue={comment.message}
					placeholder="Enter your comments here..."
					ref={register({ required: true, min: 3 })}
				></textarea>
				{errors.message && <div className="invalid-feedback d-block">This field is required</div>}
			</div>
			<div className="d-flex justify-content-end">
				<button className="btn btn-light me-2" type="button" onClick={onCancel}>
					<span className="ms-2">Cancel</span>
				</button>
				<button className="btn btn-primary" disabled={loading || showLoading} type="submit">
					<FontAwesomeIcon
						icon={(loading || showLoading) ? faSpinner : faCloudUploadAlt}
						spin={loading || showLoading}
					/>
					<span className="ms-2">Save</span>
				</button>
			</div>
		</form>
	)
}

const CommentForm = ({ commentable_id, commentable_type, type, onUpdate }) => {

    const { commentServices, authServices, data, setAppState } = useContext(AppContext)
    const { app } = data
    const { user } = app
    const [ isOpen, setIsOpen ] = useState(false)
    const mountedRef = useRef(true)

    const [ showDialog, setShowDialog ] = useState(false)
    const [ dialogOptions, setDialogOptions ] = useState({})
    const [ articleLoading, setArticleLoading ] = useState(false)
    const [ article, setArticle ] = useState(null)
    const [ loading, setLoading ] = useState(false)

    const { register, handleSubmit, watch, errors, setError, reset } = useForm();

	const addToast = useToastContext()

	useEffect(() => {
		return () => mountedRef.current = false
	}, [])

    const modalDialog = (options) => {
        return new Promise((resolve, reject) => {
            options.onButtonClick = button => {
                if(['cancel', 'close'].includes(button)){
                    setShowDialog(false)
                    reject()
                }
                if(['agree'].includes(button)){
                    setShowDialog(false)
                    resolve()
                }
            }
            options.show = showDialog
            setDialogOptions(options)
            setShowDialog(true)
        })
    }

	const onSubmit = data => {
		data.commentable_id = commentable_id
		data.commentable_type = commentable_type
		data.type = type
		setLoading(true)
		return commentServices.store(data).then(res => {
            setAppState('app.user_str_id', res.user.str_id)
            setAppState('app.user_name', res.user.full_name)
            setAppState('app.user_has_email', res.user.email ? true : false)
			reset()
			onUpdate().then(() => {
				if(mountedRef.current) setLoading(false)
			})
		}).catch(res => {
			setLoading(false);
            if(res.status == 429){
                addToast({title: 'Too Many Requests', message: 'Please try again later.', type: 'warning'})
            }else if(res.data){
                if(res.data.message) addToast({title: 'Error', message: res.data.message, type: 'danger'})
                if(res.data.errors){
                    const keys = Object.keys(res.data.errors)
                    for(var i = 0; i < keys.length; i ++){
                        setError(keys[i], {
                            type: "manual",
                            message: res.data.errors[keys[i]].join('. ')
                        })
                    }
                }
            }			
		})
	}

	return (
		<form className="mb-3" onSubmit={handleSubmit(res => {
			if(!app.agreeToCommentingRules){
	            if(!article){
	                setArticleLoading(true)
	                loadArticle('commenting-rules').then(res => {
	                    setArticle(res.article)
	                    setArticleLoading(false)
	                })
	            }
	            modalDialog({ title: 'Confirm', buttons: ['cancel', 'agree'] }).then(() => {
					setAppState('app.agreeToCommentingRules', true)
					return onSubmit(res)
	            }).catch(res => {})
			}else{
				onSubmit(res)
			}
		})}>
			{!app.user_has_email &&
				<>
					<div className="mb-2">
						<label>Your name</label>
						<input
							name="name"
							type="text"
							className="form-control"
							placeholder="Enter your name..."
							ref={register({ required: true, min: 2 })}
						/>
						{errors.name &&
							<div className="invalid-feedback d-block">
								{errors.name.message ? errors.name.message : 'This field is required'}
							</div>
						}
					</div>
					<div className="mb-2">
						<label>
							<OverlayTrigger
								placement="right"
								delay={{ show: 250, hide: 400 }}
								overlay={(props) => (
									<Popover {...props} className="px-2 py-1">
										Your email address will be used to create your account but will not show in any public area.
									</Popover>
								)}
							>
								<span>
									<span className="me-2" style={{borderBottom: '1px gray dashed'}}>Your email address</span>
									<small className="text-muted"><FontAwesomeIcon icon={faQuestionCircle}/></small>
								</span>
							</OverlayTrigger>

						</label>
						<input
							name="email"
							type="email"
							className="form-control"
							placeholder="Enter your email address..."
							ref={register({ required: true })}
						/>
						{errors.email &&
							<div className="invalid-feedback d-block">
								{errors.email.message ? errors.email.message : 'This field is required'}
							</div>
						}
					</div>
				</>
			}
			<div>
				{app.user_has_email ? (
					<label>Comment as {app.user_name}</label>
				) : (
					<label>Your comment</label>
				)}
				<textarea
					className="form-control mb-2"
					rows="2"
					name="message"
					defaultValue=""
					placeholder="Enter your comments here..."
					ref={register({ required: true, min: 3 })}
				></textarea>
				{errors.message &&
					<div className="invalid-feedback d-block">
						{errors.message.message ? errors.message.message : 'This field is required'}
					</div>
				}
			</div>
			<div className="d-flex justify-content-between flex-wrap">
				<div className="me-2 d-flex flex-column justify-content-center">
					{app.user_has_email && !authServices.isLoggedIn() &&
						<small>Because you are logged out you will need to receive a verification email in order to confirm it. <br /></small>
					}
					<small>By clicking submit, you agree to the <a href="/article/commenting-rules" target="_blank">commenting rules</a>.</small>
				</div>
				<div>
					<button
						className="btn btn-primary"
						disabled={loading}
					>
						<FontAwesomeIcon
							spin={loading}
							icon={loading ? faSpinner : faPaperPlane}
						/>
						<span className="ms-2">Send</span>
					</button>
				</div>
			</div>
		    <Modal {...{...dialogOptions, data: {agree_disabled: articleLoading}}} show={showDialog}>
                <div className="card-body">
                    {articleLoading &&
                        <div className="my-5 text-center">
                            <FontAwesomeIcon icon={faSpinner} spin />
                        </div>
                    }
                    {article &&
                        <div>
                            <h3 className="">{article.name}</h3>
                            <div className="lead">{ReactHtmlParser(article.description)}</div>
                            <div className="">{ReactHtmlParser(article.text)}</div>
                        </div>
                    }
                </div>
            </Modal>
		</form>
	)
}

export default ({ history, commentable_id, commentable_type, type, privacy, ownItem, onLoad }) => {

    const { commentServices, data, setAppState } = useContext(AppContext)
    const { app } = data

    const [ loading, setLoading ] = useState(true)
    const [ comments, setComments ] = useState({})

    const addToast = useToastContext()

    const loadFeed = () => {
    	return new Promise((resolve, reject) => {
			setLoading(true)
			commentServices.feedAccount(commentable_id, commentable_type, type).then(res => {
				setComments(res.comments)
				onLoad(res.commentable)
				setLoading(false)
				resolve()
			}).catch(res => {
				addToast({title: 'Error', message: res.data.message, type: 'danger'})
				setLoading(false)
				reject()
			})
    	})
    }

    const checkCommentScroll = () => {
		const hash = document.location.hash.substr(1);
		const element = hash && document.getElementById(hash)
		const top = element && element.getBoundingClientRect() && element.getBoundingClientRect().top
		if(top) window.scrollTo(0, top - 15);
    }

    useEffect(() => {
		if(commentable_id){
			loadFeed().then(checkCommentScroll)
		}
    }, [ commentable_id, type ])

    if(!commentable_id) return null

    const commentsCount = comments.data && comments.data.filter(item => ![0].includes(item.status) || item.user?.str_id == app.user_str_id).length

    return (
    	<div>
			{commentsCount > 0 &&
				<>
					{comments.data.map(comment =>
						<Comment
							key={comment.id}
							comment={comment}
							onUpdate={loadFeed}
						/>
					)}
				</>
			}

			{!commentsCount && !loading &&
                <div className="alert alert-info border">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    No messages found.
                </div>
			}

			{loading &&
				<div className="m-5 text-center">
					<FontAwesomeIcon icon={faSpinner} spin />
				</div>
			}

			<div className="pt-2">
				<CommentForm
					type={type}
                    commentable_id={commentable_id}
                    commentable_type={commentable_type}
					onUpdate={loadFeed}
				/>
			</div>
    	</div>
    )
}
